// Field status on a view's elements, and the progress it rolls up to
// (guide section 19), in the field's own four statuses.
//
// A row linked to model elements shows what field tracking says about them
// and is set on the tracking board, not here; a drawing-only row is set here.
// Blocked and inspected are marks on top of a status, not statuses: a
// blocked element stays in the total, and an inspection never moves the
// percentage. Everything is append-only, so any row reads back who said what
// and when. This view's percentage is weighted by length; the schedule's is
// counted by element, and the two are labelled so they are never confused.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Drawer, Input, Modal, Progress, Select, Table, Tag, Timeline, Tooltip, message } from "antd";
import { fmtFt, levelName, viewsApi } from "./viewModel";
import { tally } from "./facets";
import { flush, pending, pendingStates, sendOrQueue } from "./outbox";
import FieldTasks from "./FieldTasks";
import DailyNote from "./DailyNote";
import { activityLabel, activityTitle } from "helpers/schedule/activityLabel";
import { matchOption } from "components/SearchSelect/searchSelect";

// The PDF render (?print=1) prints every row, never page one of three.
const PRINTING = () => { try { return new URLSearchParams(window.location.search).get("print") === "1"; } catch (e) { return false; } };

export const STATUS_LABEL = {
  projected: "Projected",
  procured: "Procured",
  being_installed: "Being installed",
  installed: "Installed",
};
export const STATUS_COLOR = { projected: "default", procured: "cyan", being_installed: "blue", installed: "green" };
const KIND_LABEL = { blocked: "Blocked", unblocked: "Unblocked", inspected: "Inspected" };

// `rows` are the rows the page's filter keeps (guide 20.1: one filter drives
// everything), so the table here lists what the drawing shows. `onStates`
// hands each row's status up for the Status facet.
export default function TrackingPanel({ projectId, manifest, view, rows: shownRows, filter, onFilter, onStates, canEdit, canAdd = false, canDelete = false, picked: pickedProp, onPicked, reloadKey = 0 }) {
  const [state, setState] = useState(null);
  // The pick is shared with the tracking sheets when the page passes it in.
  const [pickedLocal, setPickedLocal] = useState([]);
  const picked = pickedProp || pickedLocal;
  const setPicked = onPicked || setPickedLocal;
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [history, setHistory] = useState(null);
  const [issues, setIssues] = useState(null);
  const [issueId, setIssueId] = useState(undefined);
  const [checklists, setChecklists] = useState(null);
  const [checklistId, setChecklistId] = useState(undefined);
  // Guide 19.4: rows linked to schedule activities.
  const [activities, setActivities] = useState(null);
  const [activityId, setActivityId] = useState(undefined);
  const [sets, setSets] = useState([]);
  const [naming, setNaming] = useState(null);   // the name being typed for a new set
  const loadSets = useCallback(() => {
    viewsApi(projectId, `/sets?viewId=${encodeURIComponent(view.viewId)}`).then((o) => setSets(o.sets || [])).catch(() => setSets([]));
  }, [projectId, view.viewId]);
  useEffect(() => { loadSets(); }, [loadSets]);

  // Loaded when first needed: the issues a block can name, the checklists rows can hang on.
  const loadIssues = () => { if (!issues) viewsApi(projectId, `/${view.viewId}/tracking/issues`).then((o) => setIssues(o.issues)).catch(() => setIssues([])); };
  const loadChecklists = () => { if (!checklists) viewsApi(projectId, `/${view.viewId}/tracking/checklists`).then((o) => setChecklists(o.checklists)).catch(() => setChecklists([])); };

  const load = useCallback(() => {
    viewsApi(projectId, `/${view.viewId}/tracking`)
      .then(setState)
      .catch((e) => message.error(e.message));
  }, [projectId, view.viewId]);
  useEffect(() => { load(); setPicked([]); }, [load, view.version]);
  // Someone else changed a status here: re-read, keep what this person picked.
  useEffect(() => { if (reloadKey) load(); }, [reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Changes made with no signal, waiting on this device (outbox.js).
  const [queuedN, setQueuedN] = useState(() => pending(projectId, view.viewId).length);
  const sendQueued = useCallback(() => flush(projectId).then((r) => {
    if (r.sent) message.success(`${r.sent} change${r.sent === 1 ? "" : "s"} made offline sent`);
    if (r.rejected.length) message.error(`${r.rejected.length} offline change${r.rejected.length === 1 ? " was" : "s were"} refused: ${r.rejected[0].error}`);
    if (r.sent || r.rejected.length) load();
  }).catch(() => {}), [projectId, load]);
  useEffect(() => {
    const count = () => setQueuedN(pending(projectId, view.viewId).length);
    const online = () => { count(); sendQueued(); };
    window.addEventListener("bl-outbox", count);
    window.addEventListener("online", online);
    const timer = setInterval(() => { if (pending(projectId).length) sendQueued(); }, 30000);
    if (pending(projectId).length) sendQueued();
    return () => { window.removeEventListener("bl-outbox", count); window.removeEventListener("online", online); clearInterval(timer); };
  }, [projectId, view.viewId, sendQueued]);

  const byRow = useMemo(() => new Map(((state && state.elements) || []).map((e) => [e.id, e])), [state]);
  useEffect(() => {
    if (!onStates) return;
    const any = ((state && state.elements) || []).some((e) => e.at || e.source === "field");
    // what is waiting on this device shows as set, until it is sent
    const waiting = pendingStates(projectId, view.viewId);
    if (!any && !waiting.size) { onStates(null); return; }
    const m = new Map(((state && state.elements) || []).map((e) => [e.id, e.status || "projected"]));
    waiting.forEach((st, id) => m.set(id, st));
    onStates(m);
  }, [state, onStates, queuedN, projectId, view.viewId]);
  const rows = useMemo(() => (shownRows || view.rows || []).map((r) => ({ ...r, tracking: byRow.get(r.id) || { status: "projected" } })), [shownRows, view.rows, byRow]);
  const pickedRows = useMemo(() => { const p = new Set(picked); return (view.rows || []).filter((r) => p.has(r.id)); }, [picked, view.rows]);
  const pickedTally = tally(pickedRows);

  // A saved set (guide 19.2) brings back its filter AND picks its elements -
  // by element key, so it still means the same sleeves after a regeneration.
  const applySet = (id) => {
    const set = sets.find((x) => x.id === id);
    if (!set) return;
    if (onFilter && set.filter) onFilter(set.filter);
    const keys = new Set(set.elementKeys || []);
    const hit = (view.rows || []).filter((r) => r.elementKey && keys.has(r.elementKey)).map((r) => r.id);
    setPicked(hit);
    const lost = (set.elementKeys || []).length - hit.length;
    message.info(`${set.name}: ${hit.length} selected${lost > 0 ? `; ${lost} no longer in this version` : ""}`);
  };
  const saveSet = async () => {
    const name = String(naming || "").trim();
    if (!name) return;
    try {
      await viewsApi(projectId, `/${view.viewId}/sets`, { method: "POST", body: { name, rowIds: picked, filter } });
      message.success(`Saved "${name}" (${pickedTally.rows} rows)`);
      setNaming(null); loadSets();
    } catch (e) { message.error(e.message); }
  };
  const removeSet = async (id) => {
    try { await viewsApi(projectId, `/sets/${id}`, { method: "DELETE" }); loadSets(); } catch (e) { message.error(e.message); }
  };

  const post = async (path, body, done) => {
    setBusy(true);
    try {
      // Status, block and inspect work with no signal (guide 19.7): kept on
      // this device and sent, in order, when the connection is back.
      const out = await sendOrQueue(projectId, view.viewId, path, { rowIds: picked, note, ...body });
      if (out.queued) message.info(`No connection: saved on this device (${picked.length} rows). It is sent when you are back online.`);
      else message.success(done(out));
      setPicked([]); setNote("");
      if (!out.queued) load();
    } catch (e) { message.error(e.message); } finally { setBusy(false); }
  };
  const set = (status) => post("status", { status }, (out) => {
    const skipped = (out.refusedLinked || []).length;
    return `${out.written} marked ${STATUS_LABEL[status].toLowerCase()}${skipped ? `; ${skipped} linked to the model left alone - set those on the tracking board` : ""}`;
  });
  const block = (blocked) => post("blocked", { blocked, issueId: blocked ? issueId : undefined },
    (out) => `${out.written} ${blocked ? "blocked" : "unblocked"}${blocked && issueId ? "; closing that issue clears it" : ""}`);
  const attach = () => post("checklist", { checklistId }, (out) => `${out.attached} attached to the checklist${out.inspected ? `; it has already passed, so ${out.inspected} marked inspected` : "; they are marked inspected when it passes"}`);
  const inspect = () => post("inspected", {}, (out) => `${out.written} marked inspected; the percentage does not change`);
  const loadActivities = () => { if (!activities) viewsApi(projectId, `/${view.viewId}/tracking/activities`).then((o) => setActivities(o.activities || [])).catch(() => setActivities([])); };
  const linkActivity = async (detach) => {
    setBusy(true);
    try {
      const out = await viewsApi(projectId, `/${view.viewId}/tracking/activity`, { method: "POST", body: { rowIds: picked, ganttItemId: activityId, detach } });
      message.success(detach ? `${out.detached} unlinked from ${out.activity}` : `${out.linked} linked to ${out.activity}`);
      load();
    } catch (e) { message.error(e.message); } finally { setBusy(false); }
  };

  const openHistory = async (row) => {
    setHistory({ row, events: null });
    try {
      const out = await viewsApi(projectId, `/${view.viewId}/tracking/history?rowId=${encodeURIComponent(row.id)}`);
      setHistory({ row, events: out.events });
    } catch (e) { setHistory({ row, events: [], error: e.message }); }
  };

  const p = (state && state.progress) || null;
  const pages = (state && state.pages) || [];

  return (
    <section className="g-panel g-tracking">
      <div className="g-viewhead">
        <div>
          <h2 className="g-h2">Field status
            {queuedN > 0 && (
              <Tooltip title="Made with no connection; kept on this device and sent in order when the connection is back">
                <Tag color="orange" style={{ marginLeft: 8 }}>{queuedN} unsynced</Tag>
                <Button size="small" type="link" onClick={sendQueued}>Send now</Button>
              </Tooltip>
            )}
          </h2>
          <p className="g-sub">
            What is installed on site, element by element, in the field's own four statuses. Rows linked to the model show what field
            tracking says; drawing-only rows are set here. Every change is its own event with your name on it, so nothing is overwritten.
            {p ? ` This view is weighted ${p.basis === "length" ? "by length" : "by count"}; the schedule counts elements.` : ""}
          </p>
        </div>
      </div>

      {p && (
        <div className="g-strip">
          <div>
            <b>{p.percent}%</b><span>{p.basis === "length" ? `by length, of ${fmtFt(p.total)} ft` : `by count, of ${p.total} items`}</span>
          </div>
          {Object.entries(p.counts || {}).filter(([, n]) => n > 0).map(([k, n]) => (
            <div key={k}><b>{n}</b><span>{(STATUS_LABEL[k] || k).toLowerCase()}</span></div>
          ))}
          {p.blocked > 0 && <div><b>{p.blocked} of {p.elements}</b><span>blocked</span></div>}
          {p.inspected > 0 && <div><b>{p.inspected}</b><span>inspected</span></div>}
        </div>
      )}

      {state && Object.keys(state.byLevel || {}).length > 1 && (
        <div className="g-levelbars">
          {Object.entries(state.byLevel).map(([lvl, s]) => (
            <div key={lvl} className="g-levelbar">
              <span>{lvl === "unlocated" ? "Not placed" : levelName(manifest, lvl)}</span>
              <Progress percent={s.percent} size="small" status={s.blockedShare > 0 ? "exception" : undefined} />
              <span className="g-tsub">{s.elements} element{s.elements === 1 ? "" : "s"}</span>
            </div>
          ))}
        </div>
      )}

      <div className="g-actions g-noprint" style={{ margin: "12px 0 4px" }}>
        <Button size="small" onClick={() => setPicked(rows.map((r) => r.id))} disabled={!rows.length}>Select all shown ({rows.length})</Button>
        <Select size="small" allowClear placeholder={sets.length ? "Saved selection sets" : "No saved sets yet"} style={{ width: 240 }}
                value={undefined} disabled={!sets.length} onChange={applySet}
                options={sets.map((x) => ({ value: x.id, label: `${x.name} · ${(x.counts && x.counts.rows) || x.elementKeys.length} rows` }))} />
        {canAdd && <Button size="small" disabled={!picked.length} onClick={() => setNaming("")}>Save selection as a set</Button>}
        {canDelete && sets.length > 0 && (
          <Select size="small" placeholder="Remove a set" style={{ width: 170 }} value={undefined}
                  onChange={(id) => Modal.confirm({ title: `Remove "${(sets.find((x) => x.id === id) || {}).name}"?`, okText: "Remove", onOk: () => removeSet(id) })}
                  options={sets.map((x) => ({ value: x.id, label: x.name }))} />
        )}
      </div>
      {picked.length > 0 && (
        <div className="g-selfoot" role="status">
          <b>{pickedTally.rows} selected</b> · quantity {pickedTally.qty} · {pickedTally.lengthFt.toLocaleString()} ft
          {pickedTally.measured < pickedTally.rows ? <span className="g-tsub"> ({pickedTally.rows - pickedTally.measured} without a length)</span> : null}
        </div>
      )}
      <Modal open={naming !== null} title="Save this selection as a set" okText="Save" onCancel={() => setNaming(null)} onOk={saveSet}>
        <p className="g-tsub">Saved for everyone on the project, with the filter it came from. Reuse it for a task, an inspection or a daily report.</p>
        <label htmlFor="set-name">Name
          <Input id="set-name" autoFocus value={naming || ""} maxLength={120} placeholder={'e.g. "L2 west sleeves, week 42"'} onChange={(e) => setNaming(e.target.value)} onPressEnter={saveSet} />
        </label>
        <p className="g-tsub">{pickedTally.rows} rows · quantity {pickedTally.qty} · {pickedTally.lengthFt.toLocaleString()} ft</p>
      </Modal>

      {canEdit && (
        <div className="g-actions g-noprint" style={{ margin: "4px 0 12px" }}>
          <Input placeholder="A note for this update (optional)" value={note} onChange={(e) => setNote(e.target.value)} style={{ maxWidth: 320 }} />
          {["procured", "being_installed", "installed", "projected"].map((s) => (
            <Button key={s} size="small" loading={busy} disabled={!picked.length}
                    type={s === "installed" ? "primary" : "default"} onClick={() => set(s)}>
              {s === "projected" ? "Back to projected" : STATUS_LABEL[s]}{picked.length ? ` (${picked.length})` : ""}
            </Button>
          ))}
          <Select filterOption={matchOption} size="small" allowClear showSearch placeholder="for an issue" style={{ width: 200 }} value={issueId}
                  onDropdownVisibleChange={(o) => o && loadIssues()} onChange={setIssueId}
                  optionFilterProp="label" options={(issues || []).map((i) => ({ value: i.id, label: `${i.label ? `${i.label} ` : ""}${i.title}` }))} />
          <Tooltip title="Blocked work stays in the total; this only marks it. Name the issue and closing it clears the block.">
            <Button size="small" danger loading={busy} disabled={!picked.length} onClick={() => block(true)}>Block</Button>
          </Tooltip>
          <Button size="small" loading={busy} disabled={!picked.length} onClick={() => block(false)}>Unblock</Button>
          <Tooltip title="An inspection is its own fact and never moves the install percentage">
            <Button size="small" loading={busy} disabled={!picked.length} onClick={inspect}>Mark inspected</Button>
          </Tooltip>
          <Select filterOption={matchOption} size="small" allowClear showSearch placeholder="a checklist" style={{ width: 200 }} value={checklistId}
                  onDropdownVisibleChange={(o) => o && loadChecklists()} onChange={setChecklistId}
                  optionFilterProp="label" options={(checklists || []).map((c) => ({ value: c.id, label: `${c.title}${c.status === "complete" ? " (passed)" : ""}` }))} />
          <DailyNote projectId={projectId} view={view} onApplied={load} />
          <Select filterOption={matchOption} size="small" allowClear showSearch placeholder="an activity" style={{ width: 220 }} value={activityId}
                  onDropdownVisibleChange={(o) => o && loadActivities()} onChange={setActivityId} aria-label="Schedule activity"
                  optionFilterProp="label" options={(activities || []).map((a) => ({ value: a.id, label: `${activityLabel(a)}${a.phase ? ` (${a.phase})` : ""}`, title: activityTitle(a) }))} />
          <Tooltip title="The activity's progress here is read from its rows; the schedule's own field number stays the schedule's">
            <Button size="small" loading={busy} disabled={!picked.length || !activityId} onClick={() => linkActivity(false)}>Link</Button>
          </Tooltip>
          <Button size="small" loading={busy} disabled={!picked.length || !activityId} onClick={() => linkActivity(true)}>Unlink</Button>
          <Tooltip title="When that checklist passes, these rows are marked inspected">
            <Button size="small" loading={busy} disabled={!picked.length || !checklistId} onClick={attach}>Attach</Button>
          </Tooltip>
          {picked.length > 0 && <Button size="small" type="text" onClick={() => setPicked([])}>Clear</Button>}
        </div>
      )}

      <div className="g-tablewrap">
      <Table size="small" rowKey="id" dataSource={rows} pagination={rows.length > 50 && !PRINTING() ? { pageSize: 50 } : false}
             rowSelection={{ selectedRowKeys: picked, onChange: setPicked }}
             columns={[
               { title: "Row", dataIndex: "id", width: 90 },
               { title: "Level", dataIndex: "level", width: 90, render: (l) => levelName(manifest, l) },
               { title: "Type", dataIndex: "type", render: (t, r) => `${t}${r.size ? ` · ${r.size}` : ""}` },
               { title: "Qty", dataIndex: "qty", align: "right", width: 60 },
               { title: "Length", dataIndex: "lengthFt", align: "right", width: 90, render: (v, r) => (v == null ? <span className="g-tsub">—</span> : `${fmtFt(v * (r.qty || 1))} ft`) },
               {
                 title: "Status", dataIndex: "tracking", width: 220,
                 render: (t, r) => (
                   <span>
                     <Tag color={STATUS_COLOR[t.status]}>{STATUS_LABEL[t.status] || t.status}</Tag>
                     {t.blocked && (
                       <Tooltip title={t.blockedNote || (t.blockedByChecklist ? "Blocked by a checklist that was sent back" : t.blockedBy ? "Blocked by an open issue; closing it clears this" : "Blocked")}>
                         <Tag color="red">{t.blockedByChecklist ? "Blocked · checklist" : "Blocked"}</Tag>
                       </Tooltip>
                     )}
                     {t.inspectedAt && <Tag color="geekblue">Inspected</Tag>}
                     {t.source === "field" && (
                       <Tooltip title="Linked to model elements: this is what field tracking says, and it is set on the tracking board">
                         <span className="g-tsub">{t.installed} of {t.elements} installed · field tracking </span>
                       </Tooltip>
                     )}
                     {t.at && (
                       <Tooltip title={`${t.byName || "someone"} · ${String(t.at).slice(0, 16).replace("T", " ")}${t.note ? ` · ${t.note}` : ""}`}>
                         <button type="button" className="g-linkbtn" onClick={() => openHistory(r)}>{t.events || 1} update{(t.events || 1) === 1 ? "" : "s"}</button>
                       </Tooltip>
                     )}
                   </span>
                 ),
               },
             ]} />
      </div>

      {state && (state.activities || []).length > 0 && (
        <div className="g-tsub" style={{ margin: "6px 0" }}>
          <b>Activities on this view:</b>{" "}
          {state.activities.map((a) => `${a.name} · ${a.rows} row${a.rows === 1 ? "" : "s"} · ${a.percent}%`).join("; ")}
        </div>
      )}
      <FieldTasks projectId={projectId} view={view} picked={picked} elements={(state && state.elements) || []} canEdit={canEdit} canAdd={canAdd}
                  onChanged={load} onPicked={setPicked} />

      {pages.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <b>Sheet pages</b>
          <p className="g-tsub">Made when this view was published. A page points at the view, so a status set here shows on every page that includes the element.</p>
          {pages.map((pg) => (
            <Tag key={pg.pageId} color={pg.kind === "default" ? "default" : "blue"}>{pg.title}</Tag>
          ))}
        </div>
      )}

      <Drawer open={!!history} width={520} onClose={() => setHistory(null)}
              title={history ? `${history.row.id} · ${history.row.type}` : ""}>
        {history && history.events == null && <p className="g-tsub">Reading the history…</p>}
        {history && history.events && history.events.length === 0 && <p className="g-tsub">Nothing has been recorded against this element yet.</p>}
        {history && history.events && history.events.length > 0 && (
          <Timeline items={history.events.map((e) => ({
            color: e.kind === "blocked" ? "red" : e.status === "installed" ? "green" : "blue",
            children: (
              <div>
                <div><b>{e.kind && e.kind !== "status" ? KIND_LABEL[e.kind] : (STATUS_LABEL[e.status] || e.status)}</b> {e.kind === "status" && e.previous && e.previous !== e.status ? <span className="g-tsub">from {STATUS_LABEL[e.previous] || e.previous}</span> : null}</div>
                <div className="g-tsub">{String(e.at).slice(0, 16).replace("T", " ")} · {e.byName || "unknown"} · {e.source}</div>
                {e.note ? <div>{e.note}</div> : null}
                {(e.keyHistory || []).length > 0 ? <div className="g-tsub">Carried over when the view was regenerated.</div> : null}
              </div>
            ),
          }))} />
        )}
      </Drawer>
    </section>
  );
}
